import type { IPurchaseIntentResponse } from '@escloud/types';
import {
    BillingAddressElement,
    PaymentElement,
    TaxIdElement,
    useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { AccountRecallCard } from './account-recall-card';
import { EmailCard } from './email-card';
import { OrderSummary } from './order-summary';

import { OnboardingShell, StepTitle } from '@/components/onboarding/onboarding-shell';
import { WizardStepper } from '@/components/onboarding/wizard-stepper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth0, useTheme } from '@/context';
import { useUpdateProfile } from '@/hooks/use-current-user';
import { useRecordAcceptance, useRequiredAcceptances } from '@/hooks/use-legal';
import { LEGAL_DOCUMENTS } from '@/legal';
import { errorIds, logError } from '@/lib/log-error';
import { activatingHref, SUBSCRIBE_STEPS, stepCounter } from '@/lib/onboarding-funnel';
import { stripeAppearance } from '@/lib/stripe-appearance';

/**
 * Step 2 of the purchase funnel (ESCLOUD-332), rendered inside a
 * `CheckoutElementsProvider`. Stripe owns the card, billing address and
 * (for organizations) VAT id blocks, and computes the total from them - this
 * screen never does its own tax arithmetic.
 *
 * The EULA acceptance is recorded through OUR API (with this request's IP and
 * user agent, in the intent's region) before `checkout.confirm()` runs: the
 * acceptance must exist even if Stripe never returns a valid payment method. A
 * decline and any other confirm failure are told apart and shown in place,
 * with the details kept, and nothing is retried automatically.
 */
export function PaymentScreen({
    intent,
    regionName,
    onSessionInvalid,
}: {
    intent: IPurchaseIntentResponse;
    regionName: string;
    onSessionInvalid: (message: string) => void;
}) {
    const navigate = useNavigate();
    const { user } = useAuth0();
    const { resolvedTheme } = useTheme();
    const region = intent.region;

    const sdk = useCheckoutElements();
    const requiredAcceptances = useRequiredAcceptances({ region });
    const recordAcceptance = useRecordAcceptance({ region });
    const updateProfile = useUpdateProfile({ region });

    const [eulaAccepted, setEulaAccepted] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmError, setConfirmError] = useState<{ kind: 'declined' | 'failed'; message: string } | null>(
        null
    );
    const [email, setEmail] = useState(user?.email ?? '');

    // Guards against reporting the same session error twice: the SDK keeps
    // returning the same `{ type: 'error' }` value across re-renders, and
    // `onSessionInvalid`'s identity can change (it closes over state on the
    // page above) without the underlying error changing.
    const reportedSessionErrorRef = useRef<string | null>(null);
    useEffect(() => {
        if (sdk.type !== 'error') {
            // Leaving the error state (a replacement session is loading) arms the
            // report again: the next failure is a new fact even with the same text.
            reportedSessionErrorRef.current = null;
            return;
        }
        if (reportedSessionErrorRef.current === sdk.error.message) return;
        reportedSessionErrorRef.current = sdk.error.message;
        onSessionInvalid(sdk.error.message);
    }, [sdk, onSessionInvalid]);

    const checkout = sdk.type === 'success' ? sdk.checkout : null;

    // Latest-value ref rather than an effect dependency: `checkout` is a fresh
    // object most renders, and the appearance push below must fire on a THEME
    // change only, not on every render that happens to produce a new checkout
    // reference.
    const checkoutRef = useRef(checkout);
    checkoutRef.current = checkout;
    const isFirstThemeRender = useRef(true);
    useEffect(() => {
        if (isFirstThemeRender.current) {
            // The provider was already created with this theme's appearance;
            // pushing it again on mount would be redundant.
            isFirstThemeRender.current = false;
            return;
        }
        checkoutRef.current?.changeAppearance(stripeAppearance(resolvedTheme));
    }, [resolvedTheme]);

    const requiredEula = requiredAcceptances.data?.find((doc) => doc.documentType === 'eula');
    // Gate strictness: an unanswered or errored gate query means not proven
    // accepted, so it fails closed rather than admitting a still-pending answer.
    const consentRequired =
        requiredAcceptances.isPending || requiredEula !== undefined || requiredAcceptances.isError;
    const eulaVersion = requiredEula?.documentVersion ?? LEGAL_DOCUMENTS.eula.version;

    const canSubscribe =
        checkout !== null &&
        checkout.canConfirm &&
        !requiredAcceptances.isPending &&
        (!consentRequired || eulaAccepted) &&
        !submitting;
    const total =
        checkout && checkout.tax.status === 'ready'
            ? checkout.total.total.amount
            : checkout?.total.subtotal.amount;
    const interval = checkout?.recurring?.interval ?? 'month';
    const isOrganization = intent.tenantType === 'organization';

    // Display only. The session is bound to the pre-created customer, so
    // Checkout refuses `updateEmail` (it throws) - the API already moved that
    // customer's email when it accepted the change (ESCLOUD-334).
    const onEmailChanged = (address: string) => setEmail(address);

    const subscribe = async () => {
        if (!canSubscribe || checkout === null) return;
        setSubmitting(true);
        setConfirmError(null);

        if (consentRequired) {
            try {
                await recordAcceptance.mutateAsync({ documentType: 'eula', documentVersion: eulaVersion });
            } catch (error) {
                toast.error(
                    error instanceof Error && error.message
                        ? error.message
                        : 'Your acceptance could not be recorded. Please try again.'
                );
                setSubmitting(false);
                return;
            }
        }

        if (marketingOptIn) {
            try {
                await updateProfile.mutateAsync({ marketingOptIn: true });
            } catch (error) {
                logError(errorIds.MARKETING_OPT_IN_SAVE_FAILED, 'Marketing opt-in could not be saved', {
                    error,
                });
                toast.warning(
                    'Your marketing preference could not be saved. You can set it later in your profile.'
                );
            }
        }

        // No `returnUrl`: the API sets `return_url` when it creates the session
        // (with the `session_id` placeholder activating reads), and Stripe
        // refuses a confirm() that provides it again.
        let result: Awaited<ReturnType<typeof checkout.confirm>>;
        try {
            result = await checkout.confirm({ redirect: 'if_required' });
        } catch (error) {
            // Stripe.js throws (IntegrationError) rather than resolving an
            // error result on integration mistakes; without this the button
            // would stay on "Processing…" with nothing logged.
            logError(errorIds.CHECKOUT_CONFIRM_FAILED, 'Checkout confirm threw', { error });
            setConfirmError({
                kind: 'failed',
                message: 'The payment could not be started. Please try again.',
            });
            setSubmitting(false);
            return;
        }

        if (result.type === 'error') {
            const { error } = result;
            const declined = error.code === 'paymentFailed';
            logError(errorIds.CHECKOUT_CONFIRM_FAILED, 'Checkout confirm refused', {
                code: error.code,
                ...(declined && error.paymentFailed.declineCode
                    ? { declineCode: error.paymentFailed.declineCode }
                    : {}),
            });
            setConfirmError({ kind: declined ? 'declined' : 'failed', message: error.message });
            setSubmitting(false);
            return;
        }

        navigate(activatingHref(intent.id));
    };

    return (
        <OnboardingShell
            stepper={<WizardStepper steps={SUBSCRIBE_STEPS} currentKey="payment" />}
            footer={
                <>
                    <span
                        className={
                            confirmError
                                ? 'text-[13px] text-destructive'
                                : 'text-[13px] text-muted-foreground'
                        }
                    >
                        {confirmError
                            ? 'Payment not completed.'
                            : `${stepCounter(SUBSCRIBE_STEPS, 'payment')} · renews monthly, cancel any time`}
                    </span>
                    <Button type="button" size="lg" onClick={() => void subscribe()} disabled={!canSubscribe}>
                        {submitting
                            ? 'Processing…'
                            : total
                              ? `Subscribe · ${total} per ${interval}`
                              : 'Subscribe'}
                    </Button>
                </>
            }
        >
            <StepTitle title="Payment" sub="Check the details you chose, then enter your card." />

            <div className="flex flex-col gap-6">
                <AccountRecallCard intent={intent} regionName={regionName} />

                {confirmError && (
                    <div
                        role="alert"
                        className="rounded-xl border border-destructive/60 bg-destructive/5 p-5"
                    >
                        <p className="m-0 font-medium text-destructive">
                            {confirmError.kind === 'declined'
                                ? 'Your card was declined'
                                : 'The payment could not be completed'}
                        </p>
                        <p className="m-0 mt-1.5 text-sm leading-relaxed text-muted-foreground">
                            {confirmError.message}
                        </p>
                        <p className="m-0 mt-1.5 text-sm leading-relaxed text-muted-foreground">
                            {confirmError.kind === 'declined'
                                ? 'Nothing has been charged and your details above are kept. Try another card, or contact your bank: declines are decided by the issuer, not by ESCloud.'
                                : 'Nothing has been charged and your details above are kept. Check the card, address and tax details, then try again.'}
                        </p>
                    </div>
                )}

                <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
                    <div className="flex flex-col gap-6">
                        <div className="rounded-xl border border-border bg-card p-5">
                            <h2 className="m-0 mb-3 text-[15px] font-semibold text-foreground">
                                Payment method
                            </h2>
                            {checkout ? (
                                <div className="flex flex-col gap-5">
                                    <PaymentElement options={{ layout: 'tabs' }} />
                                    <div className="border-t border-border pt-4">
                                        <h2 className="m-0 mb-3 text-[13px] font-semibold text-foreground">
                                            Billing address
                                        </h2>
                                        <BillingAddressElement options={{}} />
                                    </div>
                                    {isOrganization && (
                                        <div className="border-t border-border pt-4">
                                            <TaxIdElement options={{ visibility: 'always' }} />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div data-testid="payment-loading" className="flex flex-col gap-3">
                                    <Skeleton className="h-10 w-full" />
                                    <Skeleton className="h-10 w-full" />
                                    <p className="m-0 text-sm text-muted-foreground">
                                        Preparing a secure payment session for the {regionName} region…
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="rounded-xl border border-border bg-card p-5">
                            <div className="flex items-start gap-2.5">
                                <Checkbox
                                    id="eula-accept"
                                    checked={eulaAccepted}
                                    onCheckedChange={(checked) => setEulaAccepted(checked === true)}
                                />
                                <Label
                                    htmlFor="eula-accept"
                                    className="text-sm leading-relaxed font-normal text-foreground"
                                >
                                    I accept the{' '}
                                    <Link to="/eula" target="_blank" className="text-primary">
                                        End-User Licence Agreement
                                    </Link>{' '}
                                    and have read the{' '}
                                    <Link to="/privacy-policy" target="_blank" className="text-primary">
                                        Privacy Policy
                                    </Link>
                                    . <span className="text-muted-foreground">Required.</span>
                                </Label>
                            </div>
                            <div className="mt-3 flex items-start gap-2.5">
                                <Checkbox
                                    id="marketing-opt-in"
                                    checked={marketingOptIn}
                                    onCheckedChange={(checked) => setMarketingOptIn(checked === true)}
                                />
                                <Label
                                    htmlFor="marketing-opt-in"
                                    className="text-sm leading-relaxed font-normal text-foreground"
                                >
                                    Send me product and release notes by email.
                                </Label>
                            </div>
                            <p className="mt-4 mb-0 text-[13px] leading-relaxed text-muted-foreground">
                                ESCloud is intended for qualified healthcare professionals. You will be asked
                                to confirm your qualification before first clinical use.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6">
                        <OrderSummary checkout={checkout} />
                        <EmailCard email={email} region={region} onChanged={onEmailChanged} />
                    </div>
                </div>
            </div>
        </OnboardingShell>
    );
}
